import React from 'react';
import { Link } from 'react-router-dom';

interface Breadcrumb {
  readonly label: string;
  readonly href?: string;
}

interface PageLayoutProps {
  readonly children: React.ReactNode;
  readonly title?: React.ReactNode;
  readonly subtitle?: React.ReactNode;
  readonly header?: React.ReactNode;
  readonly breadcrumbs?: Breadcrumb[];
  readonly actions?: React.ReactNode;
  readonly maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
}

export const PageLayout: React.FC<PageLayoutProps> = ({
  title,
  subtitle,
  header,
  breadcrumbs,
  children,
  actions,
  maxWidth = 'full',
}) => {
  const maxWidthClasses = {
    sm: 'max-w-2xl',
    md: 'max-w-4xl',
    lg: 'max-w-6xl',
    xl: 'max-w-7xl',
    full: 'max-w-full',
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/20 max-w-[calc(100vw - 19rem)]">
      <div className={`mx-auto px-4 sm:px-6 lg:px-8 ${maxWidthClasses[maxWidth]}`}>
        {/* Enhanced Breadcrumbs */}
        <nav className="pt-6 pb-4">
          <ol className="flex items-center space-x-2 text-sm">
            {breadcrumbs?.map((breadcrumb, index) => {
              const breadcrumbKey = `${breadcrumb.label}-${breadcrumb.href ?? index}`;

              return (
                <li key={breadcrumbKey} className="flex items-center">
                  {index > 0 && (
                    <svg
                      className="w-4 h-4 text-gray-400 mx-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  )}
                  {breadcrumb.href ? (
                    <Link
                      to={breadcrumb.href}
                      className="text-gray-600 hover:text-blue-600 transition-colors duration-200 font-medium"
                    >
                      {breadcrumb.label}
                    </Link>
                  ) : (
                    <span className="text-gray-900 font-semibold">{breadcrumb.label}</span>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>

        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{title}</h1>
              {subtitle && (
                <div className="mt-2 text-lg text-gray-600 leading-relaxed">{subtitle}</div>
              )}
            </div>
            {actions && <div className="flex items-center gap-3 flex-shrink-0">{actions}</div>}
          </div>
          {header && <div className="mt-4">{header}</div>}
        </div>

        {/* Content */}
        <div className="pb-8">{children}</div>
      </div>
    </div>
  );
};
